import Phaser from 'phaser';
import { SCENES } from '../types/game';
import { AudioManager } from '../utils/AudioManager';
import { GAME_CONFIG } from '../lib/game-config';

const REFRESH_INTERVAL_MS = 10000;

interface EventPanelResponse {
  globalBoss: {
    active: boolean;
  };
  treasury: {
    balanceStt: string;
    bossOnThresholdStt: string;
    remainingToBossOnStt: string;
    progressToBossOnPercent: number;
  };
  bounty: {
    poolStt: string;
  };
  king: {
    address: string | null;
    score: string;
    source: string;
  };
  reactivity?: {
    enabled: boolean;
    connected: boolean;
    lastEventAt: number | null;
    lastError: string | null;
  };
}

export class EventScene extends Phaser.Scene {
  private userData: any = null;
  private refreshTimer: Phaser.Time.TimerEvent | null = null;
  private statusText?: Phaser.GameObjects.Text;
  private globalBossValue?: Phaser.GameObjects.Text;
  private treasuryValue?: Phaser.GameObjects.Text;
  private treasuryHint?: Phaser.GameObjects.Text;
  private bountyValue?: Phaser.GameObjects.Text;
  private kingValue?: Phaser.GameObjects.Text;
  private reactivityValue?: Phaser.GameObjects.Text;
  private treasuryBar?: Phaser.GameObjects.Graphics;
  private treasuryBarX = 0;
  private treasuryBarY = 0;
  private treasuryBarWidth = 0;
  private treasuryBarHeight = 16;

  constructor() {
    super({ key: SCENES.EVENT });
  }

  init(data?: any) {
    if (data && data.user) {
      this.userData = data.user;
    }
  }

  preload() {
    AudioManager.getInstance().preloadAudio(this);
  }

  create() {
    const { width, height } = this.cameras.main;
    const currentMusic = AudioManager.getInstance().getCurrentMusicKey();
    if (currentMusic !== 'main-menu-music') {
      AudioManager.getInstance().playMainMenuMusic(this);
    }

    this.add.image(width / 2, height / 2, 'leaderboard_background');
    this.add.rectangle(width / 2, height / 2, width, height, 0x020617, 0.62);

    const panelWidth = Math.min(width - 90, 1030);
    const panelHeight = Math.min(height - 80, 520);
    const panelX = (width - panelWidth) / 2;
    const panelY = (height - panelHeight) / 2;

    const panelFrame = this.add.graphics();
    panelFrame.fillStyle(0x0b1220, 0.88);
    panelFrame.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 16);
    panelFrame.lineStyle(2, 0x334155, 1);
    panelFrame.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 16);

    this.add.text(width / 2, panelY + 38, 'KINGSOMNI EVENT PANEL', {
      fontFamily: 'Minecraft',
      fontSize: '34px',
      color: '#f8fafc',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.statusText = this.add.text(width / 2, panelY + 74, 'Loading event data...', {
      fontFamily: 'Minecraft',
      fontSize: '15px',
      color: '#94a3b8',
    }).setOrigin(0.5);

    const gap = 18;
    const cardW = (panelWidth - gap * 3) / 2;
    const bossCard = this.createCard(panelX + gap, panelY + 96, cardW, 110, 'GLOBAL BOSS', 0xf97316);
    const bountyCard = this.createCard(panelX + gap * 2 + cardW, panelY + 96, cardW, 110, 'BOUNTY POOL', 0xfb7185);
    const treasuryCard = this.createCard(
      panelX + gap,
      panelY + 96 + 110 + gap,
      cardW,
      170,
      'TREASURY PROGRESS',
      0x60a5fa,
    );
    const kingCard = this.createCard(
      panelX + gap * 2 + cardW,
      panelY + 96 + 110 + gap,
      cardW,
      170,
      'KING SNAPSHOT',
      0x86efac,
    );
    const reactiveCard = this.createCard(
      panelX + gap,
      panelY + panelHeight - 84,
      panelWidth - gap * 2,
      62,
      'OFF-CHAIN REACTIVITY',
      0xfacc15,
    );

    this.globalBossValue = this.add.text(bossCard.x + 18, bossCard.y + 52, '-', {
      fontFamily: 'Minecraft',
      fontSize: '28px',
      color: '#f8fafc',
      fontStyle: 'bold',
    });

    this.bountyValue = this.add.text(bountyCard.x + 18, bountyCard.y + 52, '-', {
      fontFamily: 'Minecraft',
      fontSize: '28px',
      color: '#f8fafc',
      fontStyle: 'bold',
    });

    this.treasuryValue = this.add.text(treasuryCard.x + 18, treasuryCard.y + 50, '-', {
      fontFamily: 'Minecraft',
      fontSize: '18px',
      color: '#e2e8f0',
      fontStyle: 'bold',
    });

    this.treasuryHint = this.add.text(treasuryCard.x + 18, treasuryCard.y + 135, '-', {
      fontFamily: 'Minecraft',
      fontSize: '14px',
      color: '#94a3b8',
    });

    this.treasuryBar = this.add.graphics();
    this.treasuryBarX = treasuryCard.x + 18;
    this.treasuryBarY = treasuryCard.y + 106;
    this.treasuryBarWidth = cardW - 36;
    this.drawTreasuryBar(0);

    this.kingValue = this.add.text(kingCard.x + 18, kingCard.y + 50, '-', {
      fontFamily: 'Minecraft',
      fontSize: '18px',
      color: '#e2e8f0',
      wordWrap: { width: cardW - 36 },
    });

    this.reactivityValue = this.add.text(reactiveCard.x + 18, reactiveCard.y + 35, '-', {
      fontFamily: 'Minecraft',
      fontSize: '14px',
      color: '#cbd5e1',
      wordWrap: { width: panelWidth - gap * 2 - 36 },
    });

    this.createButtons();
    void this.loadPanelData();

    this.refreshTimer = this.time.addEvent({
      delay: REFRESH_INTERVAL_MS,
      loop: true,
      callback: () => {
        void this.loadPanelData();
      },
    });
  }

  private createCard(x: number, y: number, width: number, height: number, title: string, accentColor: number) {
    const card = this.add.graphics();
    card.fillStyle(0x0f172a, 0.88);
    card.fillRoundedRect(x, y, width, height, 12);
    card.lineStyle(1, 0x334155, 1);
    card.strokeRoundedRect(x, y, width, height, 12);
    card.fillStyle(accentColor, 0.95);
    card.fillRoundedRect(x + 12, y + 8, width - 24, 4, 3);

    this.add.text(x + 16, y + 16, title, {
      fontFamily: 'Minecraft',
      fontSize: '16px',
      color: '#e2e8f0',
      fontStyle: 'bold',
    });

    return { x, y, width, height };
  }

  private createButtons() {
    const { width, height } = this.cameras.main;

    const refreshButton = this.add.text(24, height - 28, 'REFRESH NOW', {
      fontFamily: 'Minecraft',
      fontSize: '16px',
      color: '#082f49',
      backgroundColor: '#67e8f9',
      fontStyle: 'bold',
      padding: { left: 16, right: 16, top: 8, bottom: 8 },
    }).setOrigin(0, 0.5).setInteractive({ cursor: 'pointer' });
    refreshButton.on('pointerdown', () => void this.loadPanelData());
    refreshButton.on('pointerover', () => refreshButton.setScale(1.04));
    refreshButton.on('pointerout', () => refreshButton.setScale(1));

    const backButton = this.add.text(width - 24, height - 28, 'EXIT', {
      fontFamily: 'Minecraft',
      fontSize: '16px',
      color: '#fee2e2',
      backgroundColor: '#b91c1c',
      fontStyle: 'bold',
      padding: { left: 22, right: 22, top: 8, bottom: 8 },
    }).setOrigin(1, 0.5).setInteractive({ cursor: 'pointer' });
    backButton.on('pointerdown', () => this.scene.start(SCENES.HOME, { user: this.userData }));
    backButton.on('pointerover', () => backButton.setScale(1.04).setBackgroundColor('#dc2626'));
    backButton.on('pointerout', () => backButton.setScale(1).setBackgroundColor('#b91c1c'));
  }

  private drawTreasuryBar(percentRaw: number) {
    if (!this.treasuryBar) {
      return;
    }
    const percent = Math.max(0, Math.min(100, percentRaw));
    const fillWidth = (this.treasuryBarWidth * percent) / 100;

    this.treasuryBar.clear();
    this.treasuryBar.fillStyle(0x1e293b, 0.95);
    this.treasuryBar.fillRoundedRect(
      this.treasuryBarX,
      this.treasuryBarY,
      this.treasuryBarWidth,
      this.treasuryBarHeight,
      8,
    );
    this.treasuryBar.fillStyle(percent >= 100 ? 0xf97316 : 0x38bdf8, 1);
    this.treasuryBar.fillRoundedRect(
      this.treasuryBarX,
      this.treasuryBarY,
      Math.max(6, fillWidth),
      this.treasuryBarHeight,
      8,
    );
    this.treasuryBar.lineStyle(1, 0x475569, 1);
    this.treasuryBar.strokeRoundedRect(
      this.treasuryBarX,
      this.treasuryBarY,
      this.treasuryBarWidth,
      this.treasuryBarHeight,
      8,
    );
  }

  private async loadPanelData() {
    try {
      this.statusText?.setText('Syncing with backend event panel...');
      this.statusText?.setColor('#94a3b8');

      const backendBaseUrl = GAME_CONFIG.API.BACKEND_URL.replace(/\/$/, '');
      const response = await fetch(`${backendBaseUrl}/api/event-panel`, { cache: 'no-store' });
      const payload = (await response.json().catch(() => ({}))) as Partial<EventPanelResponse>;

      if (!response.ok) {
        throw new Error((payload as any)?.error || 'Failed to fetch event panel');
      }

      const globalBossActive = Boolean(payload.globalBoss?.active);
      this.globalBossValue?.setText(globalBossActive ? 'ACTIVE' : 'INACTIVE');
      this.globalBossValue?.setColor(globalBossActive ? '#f97316' : '#4ade80');

      const treasuryBalance = this.formatAmount(payload.treasury?.balanceStt ?? '0');
      const threshold = this.formatAmount(payload.treasury?.bossOnThresholdStt ?? '0');
      const remaining = this.formatAmount(payload.treasury?.remainingToBossOnStt ?? '0');
      const progressRaw = Number(payload.treasury?.progressToBossOnPercent ?? 0);
      const progress = Number.isFinite(progressRaw) ? progressRaw : 0;
      this.treasuryValue?.setText(`${treasuryBalance} / ${threshold} STT`);
      this.treasuryHint?.setText(`Progress ${progress.toFixed(2)}%  |  Need ${remaining} STT`);
      this.drawTreasuryBar(progress);

      const bountyPool = this.formatAmount(payload.bounty?.poolStt ?? '0');
      this.bountyValue?.setText(`${bountyPool} STT`);

      const kingAddress = payload.king?.address ? this.formatAddress(payload.king.address) : 'No leader yet';
      const kingScore = this.formatScore(payload.king?.score ?? '0');
      const kingSource = payload.king?.source ?? '-';
      this.kingValue?.setText(`${kingAddress}\nScore: ${kingScore}\nSource: ${kingSource}`);

      if (payload.reactivity) {
        const statusLabel = payload.reactivity.connected ? 'CONNECTED' : 'DISCONNECTED';
        const statusColor = payload.reactivity.connected ? '#4ade80' : '#f87171';
        const lastEventText =
          payload.reactivity.lastEventAt && Number.isFinite(payload.reactivity.lastEventAt)
            ? new Date(payload.reactivity.lastEventAt).toLocaleTimeString()
            : '-';
        const errorText = payload.reactivity.lastError ? ` | error: ${payload.reactivity.lastError}` : '';
        this.reactivityValue?.setText(
          `Mode: ${payload.reactivity.enabled ? 'ON' : 'OFF'} | Status: ${statusLabel} | last event: ${lastEventText}${errorText}`,
        );
        this.reactivityValue?.setColor(statusColor);
      } else {
        this.reactivityValue?.setText('Mode: unknown');
        this.reactivityValue?.setColor('#94a3b8');
      }

      this.statusText?.setText(`Last updated: ${new Date().toLocaleTimeString()}`);
      this.statusText?.setColor('#cbd5e1');
    } catch (error) {
      console.error('Failed to load event panel:', error);
      this.statusText?.setText('Failed to load event panel data');
      this.statusText?.setColor('#fca5a5');
    }
  }

  private formatAddress(address: string): string {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  private formatAmount(value: string): string {
    const number = Number(value);
    if (!Number.isFinite(number)) {
      return '0.0000';
    }
    return number.toFixed(4);
  }

  private formatScore(value: string): string {
    const score = Number(value);
    if (!Number.isFinite(score)) {
      return '0';
    }
    return Math.floor(score).toLocaleString();
  }

  shutdown() {
    this.refreshTimer?.destroy();
    this.refreshTimer = null;
  }
}
